import { textareaToRaw } from '../Textarea/utils';

/**
 * A value's description as the API wants it.
 *
 * The row that is open for editing holds a Draft.js EditorState in place of the raw text, so that
 * typing in it is not a conversion per keystroke. Every other row holds what the API returned.
 * Both are accepted here, which is what lets a form submit while a row is still open.
 *
 * @param {object} value - A staged attribute value.
 * @returns {string|null}
 */
export const attributeValueDescription = value => {
  const description = value.attribute_value_description;

  return typeof description?.getCurrentContent === 'function'
    ? textareaToRaw({ value: description }) || null
    : description || null;
};

/**
 * Whether a value has been changed since the form read it.
 *
 * Only an existing value can be modified — one that has never been saved is new, which is a state
 * of its own. Compared property by property rather than by a dirty flag so that typing something
 * and typing it back leaves the value unmarked, as it should.
 *
 * @param {object} value - A staged attribute value.
 * @param {object[]} original - The values as they were read.
 * @returns {boolean}
 */
export const isAttributeValueModified = (value, original = []) => {
  if (!value.attribute_value_id) return false;

  const before = original.find(x => x.attribute_value_id === value.attribute_value_id);
  if (!before) return false;

  return (
    (value.attribute_value_name || '') !== (before.attribute_value_name || '') ||
    (value.attribute_value_code || '') !== (before.attribute_value_code || '') ||
    value.status_id !== before.status_id ||
    attributeValueDescription(value) !== attributeValueDescription(before)
  );
};

/**
 * Drops every error the server reported about the values.
 *
 * Called when the list itself changes -- a value added, deleted or put back -- because those errors
 * describe the list as it was submitted, and one row more or fewer makes each of them point at the
 * wrong value. Mutated in place, as the other forms clear theirs: the object belongs to the side
 * dialog, and the change that prompted this is about to re-render the form anyway.
 *
 * @param {object} [formErrors]
 */
export const clearAttributeValueErrors = (formErrors = {}) => {
  Object.keys(formErrors)
    .filter(key => key.startsWith('attribute_values.'))
    .forEach(key => delete formErrors[key]);
};

/**
 * What will happen to a value when the attribute is saved: it will be created, updated, removed, or
 * nothing at all.
 *
 * One definition for the dot on the row and for the filter, so the two can never disagree about
 * what a value's state is.
 *
 * @param {object} value - A staged attribute value.
 * @param {object[]} [original] - The values as they were read.
 * @returns {'new'|'modified'|'deleted'|'unchanged'}
 */
export const attributeValueState = (value, original = []) => {
  if (value.isDeleted) return 'deleted';
  if (value.isNew) return 'new';

  // Named rather than left empty so that it is something the filter can be asked for: "which of
  // these have I not touched?" is as reasonable a question as the other three.
  return isAttributeValueModified(value, original) ? 'modified' : 'unchanged';
};

/**
 * Whether a value is one nobody has filled in and that has never been saved.
 *
 * Such a row is an abandoned addition rather than a value: dropping it is what makes opening the
 * card and changing your mind leave nothing behind. An existing value emptied out is a different
 * thing entirely -- it stays, and reports that it needs a name.
 *
 * @param {object} value - A staged attribute value, its description already converted.
 * @returns {boolean}
 */
export const isEmptyNewAttributeValue = value =>
  !value.attribute_value_id &&
  (value.attribute_value_name || '').trim() === '' &&
  (value.attribute_value_code || '').trim() === '' &&
  !value.attribute_value_description;

/**
 * What the server said is wrong with one staged value, if anything.
 *
 * The rules themselves live on the server, where the database enforces them; this only puts what it
 * reported back on the row it belongs to.
 *
 * @param {object[]} values - Every staged value.
 * @param {number} index - The one to check.
 * @param {object} [formErrors] - Server-side errors, keyed `attribute_values.<index>.<column>`.
 * @returns {{ attribute_value_name?: string, attribute_value_code?: string }}
 */
export const attributeValueErrors = (values, index, formErrors = {}) => {
  if (values[index].isDeleted) return {};

  // A server-side error names the row by its place in what was submitted, and what was submitted
  // leaves out the values marked for deletion -- so the two numberings differ as soon as one of
  // those sits above this row.
  const submittedIndex = values.slice(0, index).filter(x => !x.isDeleted).length;
  const errors = {};

  for (const column of ['attribute_value_name', 'attribute_value_code']) {
    const served = formErrors[`attribute_values.${submittedIndex}.${column}`];
    if (served) errors[column] = served;
  }

  return errors;
};
